// src/features/dashboard/components/TodayGameSection.tsx

import React from 'react';

import {
  CalendarDays,
  Clock3,
  Users,
} from 'lucide-react';

import type {
  TodayGameItem,
} from '../types/adminDashboard.types';


type TodayGameSectionProps = {
  games: TodayGameItem[];
};


const getStatusLabel = (
  status: TodayGameItem['status'],
): string => {
  switch (status) {
    case 'WAIT':
      return '예정';

    case 'RUNNING':
      return '진행중';

    case 'FINISHED':
      return '종료';

    default:
      return status;
  }
};


const getStatusClassName = (
  status: TodayGameItem['status'],
): string => {
  switch (status) {
    case 'WAIT':
      return 'bg-blue-50 text-blue-600';

    case 'RUNNING':
      return 'bg-green-50 text-green-600';

    case 'FINISHED':
      return 'bg-gray-100 text-gray-500';

    default:
      return 'bg-gray-100 text-gray-500';
  }
};


const TodayGameSection:
  React.FC<
    TodayGameSectionProps
  > = ({
    games,
  }) => {
    return (
      <section className="overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-sm">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 px-4 py-4 sm:px-5">

          <div className="flex items-center gap-3">

            <span className="grid h-10 w-10 place-items-center rounded-xl bg-main/10 text-main">
              <CalendarDays
                size={20}
              />
            </span>


            <div>
              <h2 className="text-base font-bold text-gray-900">
                오늘 게임 현황
              </h2>

              <p className="mt-0.5 text-xs text-gray-400">
                오늘 예정된 게임과 참가 현황입니다.
              </p>
            </div>

          </div>


          <span className="rounded-full bg-gray-100 px-2.5 py-1 text-xs font-bold text-gray-500">
            {games.length}개
          </span>

        </div>


        {/* Empty */}
        {games.length === 0 ? (
          <div className="flex min-h-[220px] flex-col items-center justify-center px-4 py-10 text-center">

            <span className="grid h-12 w-12 place-items-center rounded-full bg-gray-100 text-gray-400">
              <CalendarDays
                size={22}
              />
            </span>

            <strong className="mt-3 text-sm text-gray-700">
              오늘 예정된 게임이 없습니다.
            </strong>

            <p className="mt-1 text-xs text-gray-400">
              등록된 게임이 있으면 이곳에 표시됩니다.
            </p>

          </div>
        ) : (

          /* Game List */
          <div className="divide-y divide-gray-100">

            {games.map(
              (game) => {
                const statusLabel =
                  getStatusLabel(
                    game.status,
                  );

                const statusClassName =
                  getStatusClassName(
                    game.status,
                  );


                return (
                  <article
                    key={
                      game.gameId
                    }
                    className="flex flex-col gap-3 px-4 py-4 transition hover:bg-gray-50 sm:flex-row sm:items-center sm:px-5"
                  >

                    {/* Time */}
                    <div className="flex shrink-0 items-center gap-2 sm:w-20">

                      <Clock3
                        size={15}
                        className="text-gray-400"
                      />

                      <span className="text-sm font-bold text-gray-800">
                        {
                          game.gameTime
                        }
                      </span>

                    </div>


                    {/* Game */}
                    <div className="min-w-0 flex-1">

                      <div className="flex flex-wrap items-center gap-2">

                        <strong className="truncate text-sm text-gray-900">
                          {
                            game.gameTitle
                          }
                        </strong>


                        <span
                          className={`
                            shrink-0 rounded-full
                            px-2 py-0.5
                            text-[10px] font-bold
                            ${statusClassName}
                          `}
                        >
                          {
                            statusLabel
                          }
                        </span>

                      </div>


                      <div className="mt-1.5 flex items-center gap-1 text-xs text-gray-500">

                        <Users
                          size={13}
                        />

                        <span>
                          참가{' '}
                          <strong className="font-bold text-gray-700">
                            {
                              game
                                .participantCount
                            }
                          </strong>
                          명
                        </span>

                      </div>

                    </div>


                    {/* Running indicator */}
                    {game.status ===
                      'RUNNING' && (
                      <div className="flex shrink-0 items-center gap-1.5 text-xs font-semibold text-green-600">

                        <span className="relative flex h-2 w-2">

                          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-green-400 opacity-75" />

                          <span className="relative inline-flex h-2 w-2 rounded-full bg-green-500" />

                        </span>

                        LIVE

                      </div>
                    )}

                  </article>
                );
              },
            )}

          </div>
        )}

      </section>
    );
  };


export default TodayGameSection;